import React, { useState } from 'react';
import { Input, Button, Tag, Space, Typography } from 'antd';
import { GiftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface CouponInputProps {
  onApply: (code: string) => void;
  onRemove: () => void;
  /** Shaped to the quote's `coupon` field. The server does not return a name. */
  appliedCoupon?: { code: string; type?: string; value?: number } | null;
  /** The server's reason, already written for the customer to read. */
  couponError?: string | null;
  isLoading?: boolean;
  currency?: string;
  discount?: string;
}

export default function CouponInput({
  onApply,
  onRemove,
  appliedCoupon,
  couponError,
  isLoading,
  currency,
  discount
}: CouponInputProps) {
  const [couponCode, setCouponCode] = useState('');

  const handleApply = () => {
    if (couponCode.trim()) {
      onApply(couponCode.trim().toUpperCase());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  return (
    <div className="space-y-3">
      {appliedCoupon ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GiftOutlined className="text-green-600" />
              <div>
                <Text strong className="text-green-800">
                  {appliedCoupon.code}
                </Text>
                <div className="text-xs text-green-600">Offer applied</div>
              </div>
            </div>
            <Space>
              {discount && parseFloat(discount) > 0 && (
                <Tag color="green" className="text-xs">
                  -{currency}{discount}
                </Tag>
              )}
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  setCouponCode('');
                  onRemove();
                }}
                className="text-red-500 hover:text-red-700"
              />
            </Space>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Input
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => {
              setCouponCode(e.target.value);
              if (e.target.value.trim() === '') {
                onRemove();
              }
            }}
            onKeyPress={handleKeyPress}
            prefix={<GiftOutlined className="text-gray-400" />}
            className="flex-1"
            disabled={isLoading}
            status={couponError ? 'error' : ''}
          />
          <Button
            type="primary"
            onClick={handleApply}
            loading={isLoading}
            disabled={!couponCode.trim()}
            icon={<CheckOutlined />}
          >
            Apply
          </Button>
        </div>
      )}
      {couponError && !appliedCoupon && (
        <div className="text-sm text-red-600">{couponError}</div>
      )}
    </div>
  );
}
